/**
 * Module 'assistant': the native AI agent that runs Orgus with you (ADR 0041). You talk to
 * it in the console; it reads everything and changes anything through the AI capability
 * catalogue (./ai.ts), within an autonomy mode an owner or admin picks:
 *
 *   ask      every change waits for a person
 *   safe     everyday changes (operate tier) run; elevated and critical wait for a person
 *   trusted  everything but critical runs; critical waits for a person
 *   full     everything runs, critical included, audited as the assistant
 *
 * Per-capability rules (auto, ask, deny) override the mode, and the assistant never goes
 * above the role of the person who asked. Its brain is Claude Code on one of your machines,
 * signed in with your own login: no API key, nothing stored. Tool calls travel back over the
 * machine's own connection to the hub, so no token is handed to the model process.
 *
 * Browser-safe and dependency-free.
 */
import type { Id, Ts } from '../index.ts';
import type { AiRole, CapabilityTier } from './ai.ts';
import { AI_CAPABILITIES, ROLE_CEILING, TIER_LABEL, capabilityByName, tierRank } from './ai.ts';
import { agentKindLabel } from './adapters.ts';
import { startFailureCause } from './agent-health.ts';
import type { FailureCause } from './failures.ts';
import { isRegexSpace, trimEndOf } from './trim.ts';

export const ASSISTANT_EXT = 'assistant';
/** The actor id the assistant acts and is audited as. */
export const ASSISTANT_ACTOR = 'agent:assistant';

export const ASSISTANT_AUDIT_LABEL: Record<string, string> = {
  'assistant.message': 'Asked the assistant',
  'assistant.stop': 'Stopped the assistant',
  'assistant.wake': 'Started a cloud VM for the assistant',
  'assistant.approve': 'Approved an assistant step',
  'assistant.deny': 'Denied an assistant step',
  'assistant.settings': 'Changed assistant settings',
  'assistant.tool': 'Assistant used a Claude Code tool',
};

// ---------------------------------------------------------------------------
// Autonomy
// ---------------------------------------------------------------------------

export type AutonomyMode = 'ask' | 'safe' | 'trusted' | 'full';
export const AUTONOMY_MODES: readonly AutonomyMode[] = ['ask', 'safe', 'trusted', 'full'];
export const AUTONOMY_LABEL: Record<AutonomyMode, string> = {
  ask: 'Ask before every change',
  safe: 'Run everyday changes',
  trusted: 'Run all but critical',
  full: 'Full autonomy',
};
export const AUTONOMY_DESCRIPTION: Record<AutonomyMode, string> = {
  ask: 'It reads freely. Every change waits for your approval.',
  safe: 'Starting and stopping sessions, pausing pipelines and similar changes run. Changes that remove work, and critical ones, wait for approval.',
  trusted: 'Everything except critical changes runs. Shell commands, policy, secrets and sign-in changes wait for approval.',
  full: 'Everything runs without asking, critical changes included. Every step is audited and you can stop it at any time.',
};
/** The highest tier that runs without asking in each mode. null: nothing but reads. */
const AUTO_CEILING: Record<AutonomyMode, CapabilityTier> = { ask: 'read', safe: 'operate', trusted: 'elevated', full: 'critical' };

export type CapabilityRule = 'auto' | 'ask' | 'deny';
export const CAPABILITY_RULES: readonly CapabilityRule[] = ['auto', 'ask', 'deny'];
export const CAPABILITY_RULE_LABEL: Record<CapabilityRule, string> = { auto: 'Run', ask: 'Ask first', deny: 'Never' };

export type AssistantDecision = { verdict: 'auto' | 'ask' | 'deny'; why: string };

/**
 * What the assistant may do with one call: run it, ask a person, or refuse. The person who
 * asked bounds it (an operator's assistant never reaches elevated), then a per-capability
 * rule, then the mode.
 */
export function decideAssistantCall(capability: string, tier: CapabilityTier, mode: AutonomyMode, rules: Readonly<Record<string, CapabilityRule>>, askerRole: AiRole): AssistantDecision {
  const cap = capabilityByName(capability);
  if (!cap) return { verdict: 'deny', why: `${capability} is not in the catalogue.` };
  return decideByTier(capability, cap.title, tier, mode, rules, askerRole);
}

/**
 * The same decision for one of Claude Code's own tools (Bash, Edit, Read, ...), when the
 * assistant may use them: its tier from HARNESS_TOOLS, its rule under 'tool:<Name>'.
 */
export function decideHarnessTool(tool: string, mode: AutonomyMode, rules: Readonly<Record<string, CapabilityRule>>, askerRole: AiRole): AssistantDecision & { tier: CapabilityTier; title: string } {
  const t = harnessTool(tool);
  return { ...decideByTier(harnessRuleKey(tool), t.title, t.tier, mode, rules, askerRole), tier: t.tier, title: t.title };
}

function decideByTier(key: string, title: string, tier: CapabilityTier, mode: AutonomyMode, rules: Readonly<Record<string, CapabilityRule>>, askerRole: AiRole): AssistantDecision {
  const capability = key;
  const ceiling = ROLE_CEILING[askerRole] ?? 'read';
  if (tierRank(tier) > tierRank(ceiling)) {
    return { verdict: 'deny', why: `${title} needs the ${TIER_LABEL[tier].toLowerCase()} tier, and the person who asked (${askerRole}) reaches ${TIER_LABEL[ceiling].toLowerCase()}.` };
  }
  const rule = rules[capability];
  if (rule === 'deny') return { verdict: 'deny', why: `${title} is set to Never in the assistant settings.` };
  if (tier === 'read') return { verdict: 'auto', why: 'Reads always run.' };
  if (rule === 'auto') return { verdict: 'auto', why: `${title} is set to Run in the assistant settings.` };
  if (rule === 'ask') return { verdict: 'ask', why: `${title} is set to Ask first in the assistant settings.` };
  if (tierRank(tier) <= tierRank(AUTO_CEILING[mode])) return { verdict: 'auto', why: `${AUTONOMY_LABEL[mode]} runs ${TIER_LABEL[tier].toLowerCase()} changes.` };
  return { verdict: 'ask', why: `${AUTONOMY_LABEL[mode]} asks before ${TIER_LABEL[tier].toLowerCase()} changes.` };
}

/** The capabilities the assistant is offered as tools: everything not set to Never or above the asker's role. */
export function assistantTools(rules: Readonly<Record<string, CapabilityRule>>, askerRole: AiRole): string[] {
  const ceiling = ROLE_CEILING[askerRole] ?? 'read';
  return AI_CAPABILITIES.filter((c) => rules[c.name] !== 'deny' && tierRank(c.tier) <= tierRank(ceiling)).map((c) => c.name);
}

// ---------------------------------------------------------------------------
// Claude Code's own tools (the harness)
// ---------------------------------------------------------------------------

/**
 * Claude Code's built-in tools, when the assistant may use them (settings.harness), with the
 * tier each needs. Every use is asked of Orgus first (--permission-prompt-tool), decided like
 * a capability, and audited when it changes something. A tool not listed is critical.
 */
export const HARNESS_TOOLS: Readonly<Record<string, { tier: CapabilityTier; title: string }>> = {
  Read: { tier: 'read', title: 'Read a file' },
  Glob: { tier: 'read', title: 'Find files' },
  Grep: { tier: 'read', title: 'Search files' },
  LS: { tier: 'read', title: 'List a folder' },
  NotebookRead: { tier: 'read', title: 'Read a notebook' },
  WebSearch: { tier: 'read', title: 'Search the web' },
  WebFetch: { tier: 'read', title: 'Fetch a web page' },
  TodoWrite: { tier: 'read', title: 'Keep a task list' },
  Task: { tier: 'read', title: 'Start a subagent' },
  Agent: { tier: 'read', title: 'Start a subagent' },
  Skill: { tier: 'read', title: 'Use a skill' },
  Edit: { tier: 'elevated', title: 'Edit a file' },
  MultiEdit: { tier: 'elevated', title: 'Edit a file' },
  Write: { tier: 'elevated', title: 'Write a file' },
  NotebookEdit: { tier: 'elevated', title: 'Edit a notebook' },
  Bash: { tier: 'critical', title: 'Run a shell command' },
  PowerShell: { tier: 'critical', title: 'Run a PowerShell command' },
  BashOutput: { tier: 'critical', title: 'Read shell output' },
  KillShell: { tier: 'critical', title: 'Stop a shell' },
};

/** The rule key for a Claude Code tool: 'tool:Bash'. */
export function harnessRuleKey(tool: string): string {
  return `tool:${tool}`;
}

export function harnessTool(tool: string): { tier: CapabilityTier; title: string } {
  return Object.prototype.hasOwnProperty.call(HARNESS_TOOLS, tool) ? HARNESS_TOOLS[tool]! : { tier: 'critical', title: `Use ${tool.slice(0, 60)}` };
}

/** One line saying what a Claude Code tool call does, for the step and its approval card. */
export function harnessSummary(tool: string, input: Record<string, unknown>): string {
  const s = (k: string) => (typeof input[k] === 'string' ? (input[k] as string) : '');
  const v = s('command') || s('file_path') || s('notebook_path') || s('pattern') || s('path') || s('url') || s('query') || s('description') || s('prompt') || s('skill');
  const one = v.replace(/\s+/g, ' ').trim();
  return one.length > 300 ? `${one.slice(0, 300)}...` : one;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface AssistantSettings {
  enabled: boolean;
  mode: AutonomyMode;
  /** Per-capability overrides of the mode. */
  rules: Record<string, CapabilityRule>;
  /** Claude Code model alias or id; "" runs the machine's own Claude Code default (works on every account and deployment). */
  model: string;
  /** Where the assistant runs; null lets Orgus pick (the hub's own machine first). */
  machineId: Id | null;
  /** USD for one reply (one run of Claude Code). */
  maxBudgetUsd: number;
  /** Time limit for one reply, approvals included. */
  timeoutMs: number;
  /** How long a step waits for a person before it is not done. */
  approvalTimeoutMs: number;
  /**
   * Let it use Claude Code's own tools too (files, search, web, subagents, shell), each
   * asked of Orgus and decided by the mode and rules like a capability (HARNESS_TOOLS).
   */
  harness: boolean;
  /**
   * The launch account replies and AI jobs run on (ADR 0071, ADR 0107 amendment of 24 Oct 2026):
   * an account id, 'pool', or null for the workspace's default for Claude Code (its pool when the
   * pool stands in for unset, else the machine's own sign-in). Resolved and leased through the same
   * launch grant as session.start; the secret never leaves the grant.
   */
  launchAccountId: string | null;
}

export const ASSISTANT_DEFAULT_SETTINGS: AssistantSettings = {
  enabled: true,
  mode: 'safe',
  rules: {},
  model: '',
  machineId: null,
  maxBudgetUsd: 5,
  timeoutMs: 30 * 60_000,
  approvalTimeoutMs: 15 * 60_000,
  harness: false,
  launchAccountId: null,
};

export type AssistantSettingsPatch = Partial<AssistantSettings>;

const MODEL_NAME = /^[A-Za-z0-9._:\-[\]]{1,80}$/;

/** Why a settings patch is not valid, or null. */
export function assistantSettingsError(v: unknown): string | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return 'Send the settings as an object.';
  const s = v as Record<string, unknown>;
  for (const k of Object.keys(s)) if (!(k in ASSISTANT_DEFAULT_SETTINGS)) return `${k} is not an assistant setting.`;
  if (s.enabled !== undefined && typeof s.enabled !== 'boolean') return 'enabled must be true or false.';
  if (s.harness !== undefined && typeof s.harness !== 'boolean') return 'harness must be true or false.';
  if (s.mode !== undefined && !(AUTONOMY_MODES as readonly unknown[]).includes(s.mode)) return `mode must be one of ${AUTONOMY_MODES.join(', ')}.`;
  if (s.model !== undefined && (typeof s.model !== 'string' || (s.model !== '' && !MODEL_NAME.test(s.model)))) return 'That model name is not valid.';
  if (s.machineId !== undefined && s.machineId !== null && (typeof s.machineId !== 'string' || !s.machineId)) return 'machineId must be a machine id or null.';
  if (s.launchAccountId !== undefined && s.launchAccountId !== null && (typeof s.launchAccountId !== 'string' || !/^[\w-]{1,64}$/.test(s.launchAccountId))) return 'launchAccountId must be an account id, pool, or null.';
  if (s.maxBudgetUsd !== undefined && (typeof s.maxBudgetUsd !== 'number' || !(s.maxBudgetUsd > 0) || s.maxBudgetUsd > 100)) return 'The budget must be more than 0 and at most 100 USD.';
  if (s.timeoutMs !== undefined && (typeof s.timeoutMs !== 'number' || s.timeoutMs < 60_000 || s.timeoutMs > 4 * 60 * 60_000)) return 'The time limit must be between 1 minute and 4 hours.';
  if (s.approvalTimeoutMs !== undefined && (typeof s.approvalTimeoutMs !== 'number' || s.approvalTimeoutMs < 30_000 || s.approvalTimeoutMs > 2 * 60 * 60_000)) {
    return 'The approval wait must be between 30 seconds and 2 hours.';
  }
  if (s.rules !== undefined) {
    if (!s.rules || typeof s.rules !== 'object' || Array.isArray(s.rules)) return 'rules must map capability names to auto, ask or deny.';
    for (const [k, r] of Object.entries(s.rules as Record<string, unknown>)) {
      if (!capabilityByName(k) && !(k.startsWith('tool:') && /^[A-Za-z][A-Za-z0-9_]{0,59}$/.test(k.slice(5)))) return `${k} is not a capability or a Claude Code tool (tool:Name).`;
      if (!(CAPABILITY_RULES as readonly unknown[]).includes(r)) return `The rule for ${k} must be auto, ask or deny.`;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

/** idle: waiting for you. running: thinking or acting. waiting: a step waits for approval. */
export type ConversationState = 'idle' | 'running' | 'waiting' | 'failed';
export const CONVERSATION_STATE_LABEL: Record<ConversationState, string> = { idle: 'Ready', running: 'Working', waiting: 'Needs approval', failed: 'Failed' };

/** ok: ran. error: ran and failed. denied: a person or a rule said no. expired: nobody answered. */
export type StepOutcome = 'running' | 'pending' | 'ok' | 'error' | 'denied' | 'expired';
export const STEP_OUTCOME_LABEL: Record<StepOutcome, string> = {
  running: 'Running',
  pending: 'Waiting for approval',
  ok: 'Done',
  error: 'Failed',
  denied: 'Not allowed',
  expired: 'Not approved in time',
};

export interface AssistantStep {
  /** The capability, e.g. 'session.stop'. */
  capability: string;
  title: string;
  tier: CapabilityTier;
  /** The input, as the assistant sent it (clipped). */
  input: Record<string, unknown>;
  outcome: StepOutcome;
  /** One line: what happened, or why not. */
  message: string | null;
  /** A pending or decided approval. */
  approval: { id: Id; why: string; decidedBy: string | null; decidedAt: Ts | null } | null;
  auditId: Id | null;
  ms: number | null;
  /** Claude Code's tool_use id: links the call to its result and to a subagent's own steps. */
  toolUseId?: string;
  /** The tool as Claude Code named it: 'Bash', 'TodoWrite', 'mcp__orgus__session_stop'. */
  tool?: string;
  /** The subagent call (Task or Agent) this step ran inside; absent at the top level. */
  parentToolUseId?: string | null;
  /** What came back, clipped to ASSISTANT_RESULT_MAX. */
  result?: string;
  /** Claude Code's structured result, when it gives one (TodoWrite's todos, Grep's files), clipped. */
  structured?: Record<string, unknown>;
  startedAt?: Ts;
  endedAt?: Ts;
}

export type AssistantEntryKind = 'user' | 'text' | 'thinking' | 'step' | 'error' | 'note';

export interface AssistantEntry {
  id: Id;
  at: Ts;
  kind: AssistantEntryKind;
  /** user, text and thinking: the words. error and note: one line. */
  text?: string;
  /** For 'user': who wrote it. */
  by?: string;
  step?: AssistantStep;
  /** The reply this entry belongs to (absent on entries from before replies were tracked). */
  runId?: Id;
  /** text and thinking from inside a subagent. */
  parentToolUseId?: string | null;
  /** thinking: how long it took to write. */
  ms?: number;
  /** error: what the one step is ('no-account': add an account in Settings, Agent accounts). */
  code?: AssistantEntryCode;
}

/** The one step an error entry points at (the console shows its button). */
export type AssistantEntryCode = 'no-account' | 'install-failed';

/** Tokens one reply used, as Claude Code reports them. */
export interface AssistantTokens {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

/** One reply: a run of Claude Code for one message, with what it cost. */
export interface AssistantReply {
  runId: Id;
  startedAt: Ts;
  endedAt: Ts | null;
  outcome: 'running' | 'ok' | 'failed' | 'stopped';
  /** The model Claude Code ran, e.g. 'claude-opus-5-5'. */
  model: string | null;
  machineId: Id | null;
  cost: number | null;
  durationMs: number | null;
  /** Model turns inside the reply. */
  turns: number | null;
  tokens: AssistantTokens | null;
  /** Tokens in the context window at the last model turn, and the window's size. */
  context: { used: number; size: number | null } | null;
  /** The agent kind that ran this reply, e.g. 'claude-code', 'codex'. */
  agentKind?: string | null;
}

/** What the reply in flight is writing right now; never stored. */
export interface AssistantLive {
  runId: Id;
  /** Answer text since the last block that landed. */
  text: string;
  /** Thinking since the last block that landed. */
  thinking: string;
  /** A tool whose input is being written. */
  tool: string | null;
  at: Ts;
}

export interface Conversation {
  id: Id;
  title: string;
  /** The person who started it; the assistant never goes above their role. */
  by: string;
  role: AiRole;
  state: ConversationState;
  /** The run in flight, if any. */
  runId: Id | null;
  machineId: Id | null;
  model: string;
  /** The agent kind answering or that last answered, e.g. 'claude-code', 'codex'. */
  agentKind?: string | null;
  /** Claude Code's own session id, so the next message continues the same conversation. */
  claudeSessionId: string | null;
  createdAt: Ts;
  updatedAt: Ts;
  /** USD, summed over replies, as Claude Code reports it. */
  cost: number;
  turns: number;
  entries: AssistantEntry[];
  /** The last failure, when state is failed. */
  error: string | null;
  /** Each reply's model, time, tokens and cost, oldest first (absent on older conversations). */
  replies?: AssistantReply[];
  /** Pinned to the top of the list. */
  pinned?: boolean;
  /** What the reply in flight is writing; only on the wire, never stored. */
  live?: AssistantLive | null;
  /**
   * No machine was online, so Orgus is starting a stopped cloud VM of the workspace for this reply
   * (state stays running); the reply starts there once its machine comes online, or fails at `until`.
   */
  waking?: AssistantWaking | null;
  /**
   * The machine picked for the reply has no Claude Code: Orgus is installing it there (module
   * 'agent-install'; state stays running) and the reply starts once it is installed, or fails.
   */
  installing?: AssistantInstalling | null;
}

/** Claude Code being installed for a reply (Conversation.installing). */
export interface AssistantInstalling {
  machineId: Id;
  /** The machine's name, for "Installing Claude Code on <name>". */
  name: string;
  installId: Id;
  since: Ts;
  until: Ts;
}

/** How long a reply waits for Claude Code to be installed on its machine. */
export const ASSISTANT_INSTALL_MS = 10 * 60_000;

/** A cloud VM Orgus is starting for a reply (Conversation.waking). */
export interface AssistantWaking {
  vmId: Id;
  machineId: Id;
  /** The machine's name, for "Starting <name>, about a minute". */
  name: string;
  since: Ts;
  until: Ts;
}

/** How long a reply waits for a woken cloud VM's machine to come online. */
export const ASSISTANT_WAKE_MS = 5 * 60_000;

/** A conversation without its entries, for lists. */
export type ConversationSummary = Omit<Conversation, 'entries' | 'replies' | 'live'> & { entryCount: number; pending: number; lastText: string | null };

/** SSE 'ext' { ext: 'assistant', type: 'live' }: the reply in flight, a few times a second. */
export interface AssistantLiveEvent {
  conversationId: Id;
  live: AssistantLive | null;
}

/** PATCH /api/assistant/conversations/:id */
export interface ConversationPatch {
  title?: string;
  pinned?: boolean;
}

export const ASSISTANT_TITLE_MAX = 120;
/** A step's result, at most. */
export const ASSISTANT_RESULT_MAX = 16_000;
/** Replies one conversation keeps stats for. */
export const ASSISTANT_KEEP_REPLIES = 200;

/** state.ext.assistant */
export interface AssistantState {
  settings: AssistantSettings;
  conversations: ConversationSummary[];
  /** Steps waiting for a person, across conversations, oldest first. */
  pending: { conversationId: Id; conversationTitle: string; entryId: Id; step: AssistantStep; at: Ts }[];
}

/** POST /api/assistant/conversations and POST /api/assistant/conversations/:id/messages */
export interface AssistantMessageRequest {
  text: string;
}

/** POST /api/assistant/approvals/:approvalId */
export interface AssistantApprovalRequest {
  answer: 'allow' | 'deny';
  note?: string;
}

export const ASSISTANT_MESSAGE_MAX = 20_000;
/** Conversations a hub keeps (oldest idle ones go first). */
export const ASSISTANT_KEEP_CONVERSATIONS = 100;
/** Entries one conversation keeps (oldest first out). */
export const ASSISTANT_KEEP_ENTRIES = 500;

export function summarizeConversation(c: Conversation): ConversationSummary {
  const { entries, replies: _replies, live: _live, ...rest } = c;
  let lastText: string | null = null;
  for (let i = entries.length - 1; i >= 0 && lastText === null; i--) {
    const e = entries[i]!;
    if ((e.kind === 'text' || e.kind === 'user') && e.text) lastText = e.text.slice(0, 200);
  }
  return { ...rest, entryCount: entries.length, pending: entries.filter((e) => e.step?.outcome === 'pending').length, lastText };
}

/** A title from the first message: its first line, clipped at a word. */
export function conversationTitle(text: string): string {
  const line = (text.split(/\r?\n/).find((l) => l.trim()) ?? '').trim().replace(/\s+/g, ' ');
  if (line.length <= 60) return line || 'New conversation';
  const cut = line.slice(0, 60);
  const sp = cut.lastIndexOf(' ');
  return `${trimEndOf(sp > 30 ? cut.slice(0, sp) : cut, (ch) => ',.;:'.includes(ch) || isRegexSpace(ch))}...`;
}

// ---------------------------------------------------------------------------
// Hub <-> agent messages (ExtMessage with ext 'assistant')
// ---------------------------------------------------------------------------

/** hub -> agent, type 'run': one reply of the assistant. */
export interface AssistantRunRequest {
  runId: Id;
  prompt: string;
  model: string;
  /** Claude Code session to continue, if the conversation has one. */
  resume: string | null;
  /** The capabilities offered as tools. */
  tools: string[];
  /** Appended to Claude Code's system prompt. */
  system: string;
  maxBudgetUsd: number;
  timeoutMs: number;
  /** How long one tool call may take (an approval wait included). */
  toolTimeoutMs: number;
  /** Claude Code's own tools on, each asked of the hub first ('permission'). */
  harness: boolean;
  /**
   * Ask the hub for this run's launch grant first (module 'policy', as session.start does): the
   * hub left a ticket for this run id. `accountId`: the account it must run on; the run is refused
   * rather than falling back to the machine's own sign-in. Absent: an older hub, the machine's sign-in.
   */
  grant?: { accountId: string | null };
  /**
   * The agent to run it on: 'claude-code' (also when absent), 'codex' or 'gemini-cli'.
   * Additive, Oct 2026.
   */
  agentKind?: string;
}

/**
 * agent -> hub, type 'event': what the run is doing, read from Claude Code's stream-json
 * (with --include-partial-messages). Older agents send only 'session' and 'text'.
 */
export type AssistantRunEvent =
  | { runId: Id; kind: 'session'; sessionId: string; model?: string }
  /** A whole text block landed. */
  | { runId: Id; kind: 'text'; text: string; parentToolUseId?: string | null }
  /** A whole thinking block landed. */
  | { runId: Id; kind: 'thinking'; text: string; parentToolUseId?: string | null }
  /** Text and thinking written since the last delta (top level only), and a tool whose input is being written. */
  | { runId: Id; kind: 'delta'; text?: string; thinking?: string; tool?: string | null }
  /** The model called a tool: any tool, Claude Code's own or an orgus one. */
  | { runId: Id; kind: 'tool'; toolUseId: string; tool: string; input: Record<string, unknown>; parentToolUseId?: string | null }
  /** A tool's answer came back to the model. */
  | { runId: Id; kind: 'toolResult'; toolUseId: string; text: string; isError: boolean; structured?: Record<string, unknown> }
  /** One model turn's usage: tokens in the context window now. */
  | { runId: Id; kind: 'usage'; context: number }
  /** Claude Code summarised older messages to make room. */
  | { runId: Id; kind: 'compact'; trigger?: string };

/** agent -> hub, type 'call': the model called a tool. The hub answers with 'callResult'. */
export interface AssistantCall {
  runId: Id;
  callId: string;
  capability: string;
  input: Record<string, unknown>;
  /** The tool_use this call answers, when the agent could match it. */
  toolUseId?: string;
}

/**
 * agent -> hub, type 'permission': Claude Code wants to use one of its own tools. The hub
 * answers with 'callResult' whose text is Claude Code's permission answer:
 * {"behavior":"allow","updatedInput":{...}} or {"behavior":"deny","message":"..."}.
 */
export interface AssistantPermission {
  runId: Id;
  callId: string;
  tool: string;
  input: Record<string, unknown>;
  toolUseId?: string;
}

/** hub -> agent, type 'callResult'. */
export interface AssistantCallResult {
  runId: Id;
  callId: string;
  isError: boolean;
  /** What the model reads back. */
  text: string;
}

/** agent -> hub, type 'result': the run ended. */
export interface AssistantRunResult {
  runId: Id;
  ok: boolean;
  cancelled?: boolean;
  sessionId?: string;
  cost?: number;
  turns?: number;
  durationMs: number;
  error?: string;
  /** The model Claude Code ran. */
  model?: string;
  tokens?: AssistantTokens;
  /** The model's context window, in tokens. */
  contextWindow?: number;
  /**
   * Why it failed, when the agent knows it was the agent's own fault (not installed, signed out,
   * usage limit): the hub moves the assistant reply to the next agent. Absent: the hub reads `error`.
   */
  cause?: FailureCause;
}

/**
 * A session or reply error text says its account hit a usage limit ("You've hit your usage limit",
 * "Claude AI usage limit reached", "quota exceeded", "rate limit").
 */
export function isAssistantUsageLimit(text: string): boolean {
  if (!text) return false;
  if (/usage limit|limit reached|(?:hit|reached) your (?:\w+ )?limit|quota exceeded|exceeded your current quota|resource_exhausted|rate limit|\b429\b/i.test(text)) return true;
  return startFailureCause(500, text) === 'usage-limit';
}

/** The notice shown in conversation when a reply moves to another agent kind: "Claude Code hit its usage limit. Codex is now answering." */
export function assistantFailoverNotice(fromKind: string, toKind: string): string {
  return `${agentKindLabel(fromKind)} hit its usage limit. ${agentKindLabel(toKind)} is now answering.`;
}

/** The error shown when every enabled agent kind hit its usage limit. */
export function assistantAllLimitedError(attempts: readonly { kind: string; why?: string }[]): string {
  const reasons = attempts.map((a) => `${agentKindLabel(a.kind)}: ${(a.why ?? 'hit its usage limit').replace(/\.$/, '')}`).join('; ');
  return attempts.length > 0 ? `No agent could run it. ${reasons}.` : 'No agent could run it.';
}

/** hub -> agent, type 'cancel'. */
export interface AssistantRunCancel {
  runId: Id;
}

/** What the assistant is told about Orgus, its tools and its limits, beside the person's words. */
export function assistantSystemPrompt(o: { mode: AutonomyMode; askerId: string; askerRole: AiRole; now: Ts; hubName?: string; harness?: boolean; machineName?: string }): string {
  return [
    'You are the Orgus assistant, the native AI agent inside Orgus, a control room for AI-assisted software development:',
    'Claude Code sessions, subagents and workflows, git checkouts and worktrees, delivery pipelines, missions and backlogs, machines, cost, approvals and audit, across projects and machines.',
    'You work only through the orgus tools. Start from state_summary or needs_you_list. Projects, machines, sessions, pipelines, jobs, approvals and audit come from the named list tools, and ids come from them.',
    'For why a machine is slow or busy, and how many more sessions or heavy commands it can take, use machines_load: it splits the CPU between heavy work, sessions, projects and background (antivirus, indexer, editors) and shows the work budget. Sessions themselves cost little; the heavy commands they start (tests, typecheck, builds) are what fill a machine. For how many more agents fit and where, use machines_room; when the machine asked for is full, offer to start when there is room (mission_start with when room), on a machine with room, or now anyway, and let the person decide.',
    'For anything the named tools do not cover, never guess a path: catalog_index lists every feature area, catalog_describe gives the routes of one area (tier, role, whether a person signs off) and settings. Call those routes with api_get and api_call, and read or change settings with settings_get and settings_set. api_get reaches feature modules only, not the core state.',
    o.harness
      ? `You may also use Claude Code's own tools on ${o.machineName ?? 'this machine'} (read and search files, web search and fetch, subagents, edit files, shell commands). Orgus checks every use the same way as its own tools: reading runs, editing files is elevated, shell commands are critical. Your working folder is scratch space; give full paths for project files. Prefer the orgus tools for anything Orgus manages.`
      : 'You have no shell or file tools: only the orgus tools.',
    `You act for ${o.askerId} (${o.askerRole}) and never above that role. Autonomy mode: ${AUTONOMY_LABEL[o.mode]}. ${AUTONOMY_DESCRIPTION[o.mode]}`,
    'A step that needs approval waits until a person answers in the console; when it comes back not approved, do not try to reach the same result another way: say what you wanted to do and why.',
    'Before a change that removes work or affects many things, say in one line what you are about to do. After acting, report what changed with ids, and what still needs a person.',
    'Be brief and exact. Use plain sentences, no em dashes, no exclamation marks.',
    `Current time: ${new Date(o.now).toISOString()}.`,
  ].join('\n');
}
